import * as React from 'react';

import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {StatusBadge} from 'shells/status-badge/status-badge';

import type {User as UserFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {TYPE_APP} from './constants';
import {ViewRecoveryCodes} from './modal/view-recovery-codes';

import styles from './mount-mfa.module.css';

type Props = {
    user: UserFragment | null | undefined;
    mfaMethod?: string;
    mfaValue?: string;
    mfaEnforced: boolean;
    attemptDisableMfa: (userId?: string) => void;
};

export function MfaSetupEnabled(props: Props) {
    const [isConfirmingRemove, setIsConfirmingRemove] = React.useState(false);
    const [isShowingCodes, setIsShowingCodes] = React.useState(false);

    const disableMfaButtonRef = React.useRef();

    return (
        <>
            <div className={styles['mfa-container']}>
                <StackLegacy spacing={12}>
                    <StatusBadge level='success' text='Enabled' isInteractive={false} />
                    {!props.user ? (
                        <>
                            <span>
                                When you log in, you’ll be asked to provide a code in addition to
                                your email and password
                            </span>
                        </>
                    ) : undefined}

                    <span>
                        <b>Method: </b>
                        {props.mfaMethod === TYPE_APP
                            ? 'Authenticator app'
                            : `SMS (ending in ${props.mfaValue ? props.mfaValue : ''})`}
                    </span>

                    {!props.user ? (
                        // Show MFA buttons only for that user, not admins
                        <div className={styles['mfa-buttons']}>
                            <Button
                                onClick={() => {
                                    appHistory.push({
                                        hash: '/connect-mfa/select-method',
                                        search: location.search,
                                    });
                                }}
                                variant='text-primary'
                                size='big'
                                className={styles['two-factor-button']}
                            >
                                Change method or app
                            </Button>
                            <span className={styles.separator}>|</span>
                            <Button
                                onClick={() => {
                                    setIsShowingCodes(true);
                                }}
                                variant='text-primary'
                                size='big'
                                className={styles['two-factor-button']}
                            >
                                View recovery codes
                            </Button>
                            <tr className='section-border'></tr>
                        </div>
                    ) : undefined}

                    {props.mfaEnforced ? (
                        <div
                            data-tip='Your account requires that all users keep two-factor
                    authentication enabled'
                            className={styles['mfa-disable-container']}
                        >
                            <p className={styles['disabled-text']}>
                                Turn off two-factor authentication
                            </p>
                        </div>
                    ) : (
                        <Button
                            onClick={() => {
                                setIsConfirmingRemove(true);
                            }}
                            variant='text-danger'
                            size='big'
                            getButtonRef={disableMfaButtonRef}
                            className={styles['two-factor-button']}
                        >
                            {props.user
                                ? `Reset two-factor authentication for ${props.user.name}`
                                : 'Turn off two-factor authentication'}
                        </Button>
                    )}
                </StackLegacy>
            </div>
            {isConfirmingRemove ? (
                <Dialog
                    anchor={disableMfaButtonRef.current}
                    question={
                        props.user
                            ? `Are you sure you want to turn off two-factor authentication for ${
                                  props.user ? props.user.name : ''
                              }?`
                            : `Are you sure you want to turn off two-factor authentication?`
                    }
                    onPrimary={() => {
                        if (props.user && props.user.id) {
                            props.attemptDisableMfa(props.user.id);
                        } else {
                            props.attemptDisableMfa();
                        }

                        setIsConfirmingRemove(false);
                    }}
                    onSecondary={() => {
                        setIsConfirmingRemove(false);
                    }}
                    isWarning={true}
                    primaryText='Yes, turn off'
                    secondaryText='No, cancel'
                    location='bottom'
                />
            ) : undefined}
            {isShowingCodes ? (
                <ViewRecoveryCodes
                    isShowingCodes={isShowingCodes}
                    setIsShowingCodes={setIsShowingCodes}
                />
            ) : undefined}
        </>
    );
}
